/* Entry point for the React application. Initializes Sentry, injects Cloudflare Web Analytics,
   mounts the root <App /> inside StrictMode with a top-level Sentry ErrorBoundary. */
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

try {
  document.documentElement.style.setProperty('forced-color-adjust', 'none');
  if (typeof window !== 'undefined') {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }
} catch {
  // Ignore in environments where document is not defined
}
import './styles/index.css'
import App from './App.tsx'
import ErrorBoundary from './components/ErrorBoundary.tsx'

type CaptureExceptionFn = (error: unknown, options?: Record<string, unknown>) => void;

declare global {
  interface Window {
    captureException?: CaptureExceptionFn;
  }
}

/* Lightweight buffer for Sentry to capture errors that happen before it loads */
let _captureException: CaptureExceptionFn | null = null;
const _sentryErrorBuffer: { err: unknown; ctx?: Record<string, unknown> }[] = [];
const captureException: CaptureExceptionFn = (err, ctx) => {
  if (_captureException) {
    _captureException(err, ctx);
  } else {
    _sentryErrorBuffer.push({ err, ctx });
  }
};
if (typeof window !== 'undefined') {
  window.captureException = captureException;
}

/* Initialize Sentry error tracking dynamically once the browser is idle.
   On mobile, we defer further (5s post-load) to avoid competing with critical resources on constrained networks. */
const isMobile = typeof window !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
if (typeof window !== 'undefined') {
  const delayInit = () => {
    const runSentry = async () => {
      try {
        const sentry = await import('./lib/sentry');
        sentry.initSentry();
        _captureException = sentry.captureException;
        _sentryErrorBuffer.forEach(({ err, ctx }) => sentry.captureException(err, ctx));
        _sentryErrorBuffer.length = 0;
      } catch (e) {
        console.error("Failed to load Sentry", e);
      }
    };

    if (isMobile) {
      setTimeout(runSentry, 5000);
    } else if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        runSentry()
      })
    } else {
      setTimeout(runSentry, 2000)
    }
  }

  if (document.readyState === 'complete') {
    delayInit()
  } else {
    window.addEventListener('load', delayInit)
  }
}

const isLocalhost = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || 
   window.location.hostname === '127.0.0.1' || 
   window.location.hostname === '');

/* Register service worker so Chrome fires beforeinstallprompt for PWA install.
   Stub served in dev mode via public/sw.js; real Workbox SW in production. */
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  let refreshing = false;
  // Only reload if the page was already controlled by a service worker.
  // On first install, navigator.serviceWorker.controller is null, so we don't reload.
  const hasControllerOnLoad = !!navigator.serviceWorker.controller;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!refreshing && hasControllerOnLoad) {
      refreshing = true;
      window.location.reload();
    }
  });

  if (import.meta.env.DEV) {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.error('Service worker registration failed:', err)
    })
  } else {
    const registerSW = () => {
      navigator.serviceWorker.register('/sw.js').then((reg) => {
        reg.update();
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            reg.update();
          }
        });
      }).catch((err) => {
        console.error('Service worker registration failed:', err)
      })
    }

    const delayRegister = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(registerSW)
      } else {
        setTimeout(registerSW, 2000)
      }
    }

    if (document.readyState === 'complete') {
      delayRegister()
    } else {
      window.addEventListener('load', delayRegister)
    }
  }
}

/* Global handler for unhandled promise rejections that Sentry integrations may miss. */
window.addEventListener('unhandledrejection', (event) => {
  captureException(event.reason, {
    tags: { type: 'unhandledrejection' },
  })
})

/* Inject Cloudflare Web Analytics beacon script using the token from env vars.
   Removes the need for hardcoded tokens in index.html and allows env-specific tokens.
   We disable it on localhost and in development mode to avoid console CORS errors and dirty metrics. */
const cfToken = import.meta.env.VITE_CLOUDFLARE_ANALYTICS_TOKEN

if (cfToken && !isLocalhost && !import.meta.env.DEV) {
  const script = document.createElement('script')
  script.src = 'https://static.cloudflareinsights.com/beacon.min.js'
  script.defer = true
  script.dataset.cfBeacon = JSON.stringify({ token: cfToken })
  document.head.appendChild(script)
}

/* Create the React root on the #root DOM element and render the component tree inside
   StrictMode and a top-level custom ErrorBoundary for errors that bubble past section-level boundaries. */
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary
      fallback={
        <div className="sentry-error-fallback">
          <h2 className="sentry-error-title">Something went wrong</h2>
          <p className="sentry-error-text">Please refresh the page to continue.</p>
          <button
            onClick={() => window.location.reload()}
            className="sentry-error-button"
          >
            Refresh Page
          </button>
        </div>
      }
    >
      <App />
    </ErrorBoundary>
  </StrictMode>,
)
